// src/pages/services/SellOrRentPage.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ServiceBanner from '../../components/ServiceBanner';
import ServiceLeadForm from '../../components/ServiceLeadForm';
import { PublicAPI } from '../../api/public';
import { normalizeMediaUrl } from '../../api/base';

const Container = styled.div`
  .container { 
    width: min(1400px, 96vw); 
    margin: 0 auto; 
    padding: 0 18px; 
  }
`;

const Content = styled.div`
  padding: 40px 0;
  
  .intro {
    font-size: 18px;
    line-height: 1.6;
    color: var(--muted);
    margin-bottom: 40px;
    width: 100%;
    
    p {
      margin-bottom: 15px;
    }
    
    h2, h3 {
      color: var(--fg);
      margin-top: 25px;
      margin-bottom: 15px;
    }
  }
  
  .services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 30px;
    margin: 40px 0;
  }
  
  .service-card {
    background: var(--card);
    border: 1px solid var(--bdr);
    border-radius: var(--radius);
    padding: 30px;
    box-shadow: var(--shadow);
    
    h3 {
      color: var(--primary);
      margin-bottom: 15px;
      font-size: 24px;
    }
    
    p {
      color: var(--muted);
      line-height: 1.6;
      margin-bottom: 20px;
    }
    
    ul {
      list-style: none;
      padding: 0;
      
      li {
        padding: 8px 0;
        color: var(--fg);
        position: relative;
        padding-left: 20px;
        
        &:before {
          content: '✓';
          position: absolute;
          left: 0;
          color: var(--primary);
          font-weight: bold;
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 20px 0;
    
    .service-card {
      padding: 25px 20px;
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: var(--muted);
`;

export default function SellOrRentPage() {
  const { t, i18n } = useTranslation();
  const [service, setService] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const currentLang = i18n.language || 'ru';
  const isRu = currentLang === 'ru';

  React.useEffect(() => {
    const loadService = async () => {
      try {
        setLoading(true);
        const data = await PublicAPI.services({ locale: currentLang });
        const services = data?.items || data?.services || [];
        
        const foundService = services.find(s => {
          if (!s.link) return false;
          const serviceSlug = s.link.split('/').pop();
          return serviceSlug === 'sell-or-rent';
        });
        
        if (foundService) {
          setService(foundService);
        }
      } catch (e) {
        console.error('Failed to load service:', e);
      } finally {
        setLoading(false);
      }
    };

    loadService();
  }, [currentLang]);

  if (loading) {
    return (
      <Container>
        <div className="container">
          <LoadingWrapper>
            {t('common.loading', 'Загрузка...')}
          </LoadingWrapper>
        </div>
      </Container>
    );
  }

  const title = service 
    ? (isRu ? (service.title_ru || service.title_en) : (service.title_en || service.title_ru))
    : "Продать или сдать недвижимость";
  
  const shortDescription = service
    ? (isRu ? (service.short_description_ru || service.short_description_en) : (service.short_description_en || service.short_description_ru))
    : "Комплексные услуги по продаже и сдаче в аренду элитной недвижимости";
  
  let bannerImage = null;
  if (service) {
    if (service.cover_url) {
      bannerImage = normalizeMediaUrl(service.cover_url);
    } else if (service.media?.url) {
      bannerImage = normalizeMediaUrl(service.media.url);
    } else if (service.cover) {
      bannerImage = normalizeMediaUrl(service.cover);
    }
  }

  return (
    <Container>
      <ServiceBanner
        title={title}
        subtitle={shortDescription}
        image={bannerImage}
      />
      
      <div className="container">
        <Content>
          <div className="intro">
            <p>
              Мы предоставляем полный спектр услуг по продаже и сдаче в аренду премиальной недвижимости. 
              Наша команда экспертов обеспечит максимальную доходность от вашей собственности при минимальных затратах времени с вашей стороны.
            </p>
          </div>

          <div className="services-grid">
            <div className="service-card">
              <h3>Продажа недвижимости</h3>
              <p>Профессиональное сопровождение сделки от оценки до передачи ключей</p>
              <ul>
                <li>Маркетинговая оценка объекта</li>
                <li>Стратегия позиционирования</li>
                <li>Профессиональная фотосъемка</li>
                <li>Размещение в премиальных каналах</li>
                <li>Организация показов</li>
                <li>Переговоры с покупателями</li>
                <li>Юридическое сопровождение</li>
                <li>Сопровождение до регистрации</li>
              </ul>
            </div>

            <div className="service-card">
              <h3>Сдача в аренду</h3>
              <p>Подготовка объекта и поиск надежных арендаторов с гарантией дохода</p>
              <ul>
                <li>Подготовка объекта к сдаче</li>
                <li>Определение оптимальной ставки</li>
                <li>Поиск и отбор арендаторов</li>
                <li>Проверка платежеспособности</li>
                <li>Подготовка договоров</li>
                <li>Управление арендой</li>
                <li>Техническое обслуживание</li>
                <li>Финансовая отчетность</li>
              </ul>
            </div>
          </div>

          <ServiceLeadForm 
            type="sell_or_rent"
            serviceTitle={title}
          />
        </Content>
      </div>
    </Container>
  );
}